import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from './JobDetailNew.module.css'; 
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { 
  ArrowLeft, MapPin, Calendar, Edit2, Users, 
  MessageSquare, UserPlus, CheckCircle, Globe, Lock,
  Camera, X, ChevronLeft, ChevronRight,
  Briefcase, Star, Info, FileText, Film, Download, Play, Pause,
  Home
} from "lucide-react";
import Loader2 from '../../components/loader2';
import showToast from '../../components/ShowToast';
import { jwtDecode } from 'jwt-decode';

// --- IMPORTS ---
import BusinessProfilePureCSS from '../../profiles/BusinessProfilePureCSS'; 
import BrowseProsModal from '../../components/Projects/Modals/BrowseProsModalUpdated';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import { getOrCreateGeneralMaintenanceProjectId } from '../../services/GeneralProjectService';
import VerifiedBadge from "../../components/VerifiedBadge/VerifiedBadge";

const JobManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams();
  
  // --- STATE ---
  const [activeTab, setActiveTab] = useState('details'); 
  const [jobData, setJobData] = useState(null);
  const [leads, setLeads] = useState([]); 
  const [quotes, setQuotes] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);
  
  // Image Modal State
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalMedia, setModalMedia] = useState([]); 

  // Business Profile Panel State
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Browse Pros Modal State
  const [isBrowseProsOpen, setIsBrowseProsOpen] = useState(false);

  // Quote/Assign Modal State
  const [selectedQuote, setSelectedQuote] = useState(null); 
  const [isAssigning, setIsAssigning] = useState(false); 

  const [error, setError] = useState(null);
  const [username, setUsername] = useState('');
  const [homeUserId, setHomeUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    let uid = null;
    let uname = '';
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        uname = decodedToken.UserName || localStorage.getItem('loggedInUsername') || 'User';
        uid = decodedToken.nameid || localStorage.getItem('loggedInHomeUserId');
      } catch (e) {
        console.warn("JWT decode failed, falling back to storage:", e);
        uname = localStorage.getItem('loggedInUsername') || 'User';
        uid = localStorage.getItem('loggedInHomeUserId');
      }
    } else {
      uname = localStorage.getItem('loggedInUsername') || '';
      uid = localStorage.getItem('loggedInHomeUserId');
    }
    if (uname) setUsername(uname);
    if (uid) setHomeUserId(uid);
    if (!uid) setError("No user identified. Please log in.");
  }, []);

  // --- INITIALIZATION ---
  useEffect(() => {
    fetchData();
  }, [jobId]);

  useEffect(() => {
      if (location.state?.openInviteModal) {
          setIsBrowseProsOpen(true);
          window.history.replaceState({}, document.title);
      }
  }, [location.state]);

  const fetchData = async () => {
    try {
        const token = localStorage.getItem('token');
        let userId = 0;
        let userType = "HomeUser";
        
        if(token) {
             const rawType = localStorage.getItem('userType');
            if (rawType === "Home" || rawType === "HomeUser") {
                userType = "HomeUser";
            } else {
                userType = "BusinessUser";
            }
             userId = rawType === 'Home' ? parseInt(localStorage.getItem('loggedInHomeUserId')) : parseInt(localStorage.getItem('loggedInBusinessUserId'));
        }

        const jobRes = await axios.get(`${API_BASE_URL}/api/Jobs/getjobbyid?id=${jobId}`);
        setJobData(jobRes.data);

        const quotesRes = await axios.get(`${API_BASE_URL}/api/Jobs/quote/${jobId}`).catch(()=>({data:[]}));
        setQuotes(quotesRes.data || []);

        const leadsRes = await axios.get(`${API_BASE_URL}/api/Jobs/getjobleads?userId=${userId}&userType=${userType}`);
        const allLeads = Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data?.result || []);
        setLeads(allLeads.filter(l => String(l.jobId || l.JobId) === String(jobId)));

    } catch (err) {
        console.error("Error fetching job data", err);
    } finally {
        setIsLoading(false);
    }
  };

  const NavigateBack = async () => {
    const hasTaskId = (jobData?.taskId && jobData.taskId > 0) || (jobData?.TaskId && jobData.TaskId > 0);
    if (hasTaskId && (jobData?.projectId || jobData?.ProjectId)) {
        navigate(`/project/${jobData.projectId || jobData.ProjectId}`);
    } else {
        navigate('/my-jobs'); 
    }
  }

  const openBusinessProfile = (bizId) => {
      if (!bizId) return;
      setSelectedBusinessId(bizId);
      setIsProfileOpen(true);
  };

  const handleInviteMore = () => {
      setIsBrowseProsOpen(true);
  };

  const handleAssignJob = async () => {
      const biz = selectedQuote?.Business || selectedQuote?.business;
      const bizId = biz?.businessId || biz?.id;
      const bizName = biz?.businessName || "this professional";

      if (!selectedQuote || !bizId) {
          showToast('error', "Invalid quote data.");
          return;
      }
      
      if(!window.confirm(`Are you sure you want to hire ${bizName}? This will notify them.`)) return;

      setIsAssigning(true);
      try {
          await axios.post(`${API_BASE_URL}/api/Jobs/${jobId}/hire/${bizId}`);
          showToast('success', `Job Assigned to ${bizName}`);
          setSelectedQuote(null);
          fetchData();
      } catch (err) {
          showToast('error', "Failed to assign job.");
      } finally {
          setIsAssigning(false);
      }
  };

  const handleMarkComplete = async () => {
      if(!window.confirm("Mark this job as completed? This will close the task.")) return;
      try {
          await axios.patch(`${API_BASE_URL}/api/Jobs/updatestatus/${jobId}`, { Status: "Completed" });
          showToast('success', "Job marked as Completed!");
          fetchData();
      } catch (err) {
          showToast('error', "Failed to update status.");
      }
  };

  const getJobAttachments = () => {
    if (!jobData) return { images: [], videos: [], docs: [] };
    const rawPaths = [jobData.image1, jobData.image2, jobData.image3, jobData.image4].filter(Boolean);
    const processed = rawPaths.map(path => {
        let fullUrl = path.startsWith('http') ? path : `${API_BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
        const lower = fullUrl.toLowerCase();
        let type = 'document';
        if (/\.(jpg|jpeg|png|gif|webp)$/.test(lower)) type = 'image';
        else if (/\.(mp4|webm|mov)$/.test(lower)) type = 'video';
        return { url: fullUrl, type, name: path.split('/').pop() };
    });
    return {
        images: processed.filter(f => f.type === 'image'),
        videos: processed.filter(f => f.type === 'video'),
        docs: processed.filter(f => f.type === 'document')
    };
  };

  const { images, videos, docs } = getJobAttachments();

  const openMediaModal = (selectedUrl) => {
      const mediaItems = [...images, ...videos];
      setModalMedia(mediaItems);
      const index = mediaItems.findIndex(m => m.url === selectedUrl);
      setCurrentImageIndex(index >= 0 ? index : 0);
      setIsImageModalOpen(true);
  };

  const truncateText = (text, maxLength) => {
      if (!text) return "No bio available";
      return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  if (isLoading) return <Loader2 />;
  if (!jobData) return <div>Job not found</div>;

  const isPublic = jobData.isPublish; 
  const isAssigned = jobData.jobStatus === "Assigned" || jobData.jobStatus === "In Progress";
  const isCompleted = jobData.jobStatus === "Completed";
  const getBizFromQuote = (q) => q.Business || q.business || {};

  const getSafeId = (val1, val2) => {
      const v = val1 !== undefined ? val1 : val2;
      const parsed = parseInt(v, 10);
      return isNaN(parsed) ? 0 : parsed;
  };

  const inviteTaskId = getSafeId(jobData?.taskId, jobData?.TaskId);
  const inviteProjectId = getSafeId(jobData?.projectId, jobData?.ProjectId);
  const inviteLocationId = getSafeId(jobData?.locationId, jobData?.LocationId);


  return (
    <div className={`${styles.jobDetailNew_minHeightScreen} ${styles.jobDetailNew_bgGradientPage}`}>

    <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={jobData?.projectId ? 'projects' : ''} />
      
      {/* 1. HEADER - CLEAN GOLD BAR */}
      <header className={styles.jobDetailNew_header} style={{paddingBottom:'2rem'}}>
        <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_headerContent}`}>
          
          <div className={styles.jobDetailNew_headerLeft}>
            {/* Back Button */}
            <button onClick={() => NavigateBack()} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{color: 'white'}}>
              <ArrowLeft className={styles.jobDetailNew_textPrimaryBlueDark} />
            </button>
            
            {/* Logo Block */}
            <div className={styles.jobDetailNew_headerLogo}>
              <Home className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textPrimaryBlueDark}`} />
              <span className={styles.jobDetailNew_headerTitle}>Build Start</span>
            </div>
            
            {/* Separator */}
            <div className={styles.jobDetailNew_headerSeparator}></div>
            
            {/* CLEAN TITLE (Blue Text, No Metadata) */}
            <div>
              <h1 className={styles.jobDetailNew_headerTitle} style={{margin:0, color:'#022d6b'}}>{jobData.name}</h1>
            </div>
          </div>

          {/* Right Side Actions */}
          <div style={{display:'flex', gap:'10px'}}>
              {!isCompleted && !isAssigned && (
                  <button onClick={() => navigate(`/job-detail-new/${jobId}`)} className={styles.btnHeaderOutline}>
                      <Edit2 size={18}/> Edit
                  </button>
              )}
              {!isCompleted && !isAssigned && (
                  <button onClick={handleInviteMore} className={styles.btnHeaderPrimary}>
                      <UserPlus size={18}/> {isPublic ? 'Invite More' : 'Invite Pros'}
                  </button>
              )}
          </div>

        </div>
      </header>

      {/* 2. CONSOLE CONTENT CARD */}
      <div className={styles.jobDetailNew_container} style={{marginTop:'-1.5rem'}}>
         <div className={styles.consoleCard}>
            
            {/* 2A. METADATA STRIP (Moved from Header to here) */}
            <div style={{
                padding: '1.5rem 2rem 1rem 2rem', 
                borderBottom: '1px solid #F1F5F9',
                display: 'flex', 
                flexWrap: 'wrap',
                alignItems: 'center', 
                gap: '1.5rem',
                backgroundColor: 'white'
            }}>
                {/* Location */}
                <div style={{display:'flex', alignItems:'center', gap:'6px', color:'#64748B', fontWeight:'600', fontSize:'0.95rem'}}>
                    <MapPin size={18} color="#d97706"/> 
                    <span>{jobData.postcode}</span>
                </div>
                
                {/* Vertical Divider */}
                <div style={{width:'1px', height:'20px', backgroundColor:'#E2E8F0'}}></div>

                {/* Badges */}
                <div style={{display:'flex', gap:'10px'}}>
                    {isPublic ? 
                        <span className={styles.badgeBlue}><Globe size={14}/> Public Broadcast</span> : 
                        <span className={styles.badgeGray}><Lock size={14}/> Invite Only</span>
                    }
                    <span className={isCompleted ? styles.badgeGreen : isAssigned ? styles.badgeOrange : styles.badgeGreen}>
                        {jobData.jobStatus}
                    </span>
                </div>
            </div>

            {/* TABS */}
            <div className={styles.consoleTabs}>
                <button onClick={() => setActiveTab('details')} className={activeTab === 'details' ? styles.tabActive : styles.tabInactive}>
                    Job Details
                </button>
                <button onClick={() => setActiveTab('quotes')} className={activeTab === 'quotes' ? styles.tabActive : styles.tabInactive}>
                    Quotes Received ({quotes.length})
                </button>
                <button onClick={() => setActiveTab('invites')} className={activeTab === 'invites' ? styles.tabActive : styles.tabInactive}>
                    Invited Pros ({leads.length})
                </button>
            </div>

            <div style={{padding:'2rem'}}>
                
                {/* --- TAB: DETAILS --- */}
                {activeTab === 'details' && (
                    <div className={styles.detailsGrid}>
                        <div className={styles.leftCol}>
                            <div className={styles.sectionBlock}>
                                <h3 className={styles.sectionTitle}>Description</h3>
                                <p className={styles.descText}>{jobData.description}</p>
                            </div>

                            <div className={styles.statsRow}>
                                <div className={styles.statBox}>
                                    <span className={styles.statLabel}>Budget</span>
                                    <span className={styles.statValueGreen}>
                                        {jobData.budgetAmount ? `$${jobData.budgetAmount}` : jobData.budget}
                                    </span>
                                </div>
                                <div className={styles.statBox}>
                                    <span className={styles.statLabel}>Trade</span>
                                    <span className={styles.statValue}>{jobData.profession?.[0]?.name || "General"}</span>
                                </div>
                                <div className={styles.statBox}>
                                    <span className={styles.statLabel}>Due Date</span>
                                    <span className={styles.statValue}>{jobData.deadline}</span>
                                </div>
                            </div>

                            {/* ROW 1: IMAGES */}
                            {images.length > 0 && (
                                <div className={styles.sectionBlock} style={{marginTop:'2rem'}}>
                                    <h3 className={styles.sectionTitle}><Camera size={16}/> Photos</h3>
                                    <div className={styles.imageGrid}> 
                                        {images.map((img, idx) => (
                                            <div 
                                                key={`img-${idx}`} 
                                                className={styles.thumbWrapper} 
                                                onClick={() => openMediaModal(img.url)}
                                                style={{cursor:'pointer', borderRadius:'8px', overflow:'hidden', border:'1px solid #e2e8f0'}}
                                            >
                                                <img src={img.url} alt="Job Photo" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ROW 2: DOCUMENTS */}
                            {docs.length > 0 && (
                                <div className={styles.sectionBlock} style={{marginTop:'2rem'}}>
                                    <h3 className={styles.sectionTitle}><FileText size={16}/> Documents</h3>
                                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                        {docs.map((doc, idx) => (
                                            <div key={`doc-${idx}`} style={{
                                                display:'flex', alignItems:'center', justifyContent:'space-between', 
                                                padding:'12px', backgroundColor:'white', border:'1px solid #e2e8f0', borderRadius:'8px'
                                            }}>
                                                <div style={{display:'flex', alignItems:'center', gap:'12px', overflow:'hidden'}}>
                                                    <div style={{padding:'8px', background:'#EFF6FF', borderRadius:'6px', color:'#2563EB'}}>
                                                        <FileText size={20}/>
                                                    </div>
                                                    <span style={{fontWeight:'500', color:'#334155', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'200px'}}>
                                                        {doc.name}
                                                    </span>
                                                </div>
                                                <a 
                                                    href={doc.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className={styles.btnSmallOutline}
                                                    style={{textDecoration:'none', display:'flex', alignItems:'center', gap:'5px'}}
                                                >
                                                    <Download size={14}/> Download
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ROW 3: VIDEOS */}
                            {videos.length > 0 && (
                                <div className={styles.sectionBlock} style={{marginTop:'2rem'}}>
                                    <h3 className={styles.sectionTitle}><Film size={16}/> Videos</h3>
                                    <div className={styles.imageGrid}>
                                        {videos.map((vid, idx) => (
                                            <div 
                                                key={`vid-${idx}`} 
                                                className={styles.thumbWrapper} 
                                                onClick={() => openMediaModal(vid.url)}
                                                style={{cursor:'pointer', position:'relative', borderRadius:'8px', overflow:'hidden', backgroundColor:'#000'}}
                                            >
                                                <video src={vid.url} style={{width:'100%', height:'100%', objectFit:'cover', opacity:0.8}} />
                                                <div style={{
                                                    position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)',
                                                    color:'white', background:'rgba(0,0,0,0.5)', borderRadius:'50%', padding:'10px'
                                                }}>
                                                    <Play size={24} fill="white" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={styles.rightCol}>
                            {(isAssigned || isCompleted) && jobData.assignedBusinessId ? (
                                <div className={styles.assignedCard}>
                                    <div className={styles.assignedHeader}>
                                        <CheckCircle size={20}/> Job Assigned
                                    </div>
                                    <div className={styles.assignedBody}>
                                        <h4>{jobData.businessName || "Contractor"}</h4>
                                        <p style={{fontSize:'0.85rem', color:'#64748B'}}>Assigned on {new Date(jobData.updated_at).toLocaleDateString()}</p>
                                        
                                        <button 
                                            onClick={() => openBusinessProfile(jobData.assignedBusinessId)} 
                                            className={styles.btnSmallOutline}
                                            style={{width: '100%', marginTop:'10px'}}
                                        >
                                            <Info size={16} style={{marginRight:'5px'}}/> View Profile
                                        </button>

                                        {!isCompleted && (
                                            <button onClick={handleMarkComplete} className={styles.btnMarkComplete}>
                                                Mark Job Complete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.tipsCard}>
                                    <h4>Waiting for Quotes?</h4>
                                    <p>Inviting specific professionals increases your chance of a response by 80%.</p>
                                    <button onClick={handleInviteMore} className={styles.btnLink}>Invite Local Pros</button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* --- TAB: QUOTES --- */}
                {activeTab === 'quotes' && (
                    <div className={styles.quotesScrollContainer}>
                        {quotes.length === 0 ? (
                            <div className={styles.emptyState} style={{width:'100%'}}>No quotes received yet.</div>
                        ) : (
                            quotes.map(quote => {
                                const biz = getBizFromQuote(quote);
                                return (
                                    <div key={quote.id} className={styles.quoteColumnCard}>
                                        <div className={styles.cardAvatar}>
                                            {biz.profileImage ? (
                                                <img src={biz.profileImage} alt="Profile" />
                                            ) : (
                                                biz.businessName?.charAt(0) || "B"
                                            )}
                                        </div>
                                        <div className={styles.cardBizName}>
                                            {biz.businessName || "Unknown Business"}
                                        </div>
                                        <div className={styles.cardPrice}>
                                            ${quote.price}
                                        </div>
                                        <div className={styles.cardBio}>
                                            {truncateText(biz.description || quote.message, 90)}
                                        </div>
                                        <div className={styles.cardActions}>
                                            <button 
                                                onClick={() => openBusinessProfile(biz.businessId)}
                                                className={styles.btnCardOutline}
                                            >
                                                Profile
                                            </button>
                                            <button 
                                                onClick={() => setSelectedQuote(quote)} 
                                                className={styles.btnCardPrimary}
                                            >
                                                View Quote
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* --- TAB: INVITES --- */}
                {activeTab === 'invites' && (
                    <div className={styles.invitesGrid}>
                        {leads.map((lead, idx) => (
                            <div key={idx} className={styles.inviteCard}>
                                <div className={styles.inviteHeader}>
                                    <div className={styles.inviteAvatar}><Briefcase size={20}/></div>
                                    <div style={{flex: 1, padding: '0 12px'}}>
                                        <h4 style={{margin:0, fontSize:'1rem', color:'#022d6b', fontWeight:'700'}}>
                                            {lead.businessName || `Business ID: ${lead.businessId}`}
                                            {lead.isVerified && lead.isDocVerified && <VerifiedBadge size={18} />} 
                                        </h4>
                                        {lead.businessLocation && (
                                            <div style={{fontSize:'0.85rem', color:'#64748B', display:'flex', alignItems:'center', gap:'4px', marginTop:'2px'}}>
                                                <MapPin size={14} color="#d97706" /> 
                                                {lead.businessLocation}
                                            </div>
                                        )}
                                    </div>
                                    <span className={styles.inviteStatus}>{lead.status || "Invited"}</span>
                                </div>
                                <button 
                                    onClick={() => openBusinessProfile(lead.businessId)} 
                                    className={styles.btnSmallOutline} 
                                    style={{width:'100%', marginTop:'15px', borderColor:'#d97706', color:'#d97706'}}
                                >
                                    View Profile
                                </button>
                            </div>
                        ))}
                        {leads.length === 0 && <div className={styles.emptyState}>No professionals invited.</div>}
                    </div>
                )}
            </div>
         </div>
      </div>

      {/* --- MODALS --- */}
      {/* 1. MEDIA VIEWER */}
      {isImageModalOpen && modalMedia.length > 0 && (
        <div className={styles.imageViewerOverlay} onClick={() => setIsImageModalOpen(false)}>
            <button className={styles.closeBtn}><X size={32}/></button>
            {modalMedia.length > 1 && (
                <button className={styles.navBtnLeft} onClick={(e) => {
                    e.stopPropagation(); 
                    setCurrentImageIndex((currentImageIndex - 1 + modalMedia.length) % modalMedia.length)
                }}>
                    <ChevronLeft size={40}/>
                </button>
            )}
            <div className={styles.modalMediaContainer} onClick={e => e.stopPropagation()} style={{maxWidth:'90%', maxHeight:'90vh'}}>
                {modalMedia[currentImageIndex].type === 'video' ? (
                    <video 
                        src={modalMedia[currentImageIndex].url} 
                        controls 
                        autoPlay 
                        className={styles.zoomedImage} 
                        style={{maxHeight:'80vh', maxWidth:'100%'}}
                    />
                ) : (
                    <img 
                        src={modalMedia[currentImageIndex].url} 
                        className={styles.zoomedImage} 
                        alt="Zoomed" 
                    />
                )}
                <div style={{textAlign:'center', color:'white', marginTop:'10px'}}>
                    {currentImageIndex + 1} / {modalMedia.length}
                </div>
            </div>
            {modalMedia.length > 1 && (
                <button className={styles.navBtnRight} onClick={(e) => {
                    e.stopPropagation(); 
                    setCurrentImageIndex((currentImageIndex + 1) % modalMedia.length)
                }}>
                    <ChevronRight size={40}/>
                </button>
            )}
        </div>
      )}

      {/* 2. QUOTE DETAILS */}
      {selectedQuote && (
        <div className={styles.modalOverlay} onClick={() => setSelectedQuote(null)}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                <button onClick={() => setSelectedQuote(null)} className={styles.modalCloseBtnAbs}>
                    <X size={24}/>
                </button>
                <div className={styles.modalBody}>
                    <div className={styles.quoteBigPriceContainer}>
                        <div className={styles.quoteBigPriceLabel}>Total Quote Amount</div>
                        <div className={styles.quoteBigPrice}>${selectedQuote.price}</div>
                    </div>
                    <div className={styles.quoteDetailRow}>
                        <Calendar size={16} /> 
                        <span>Received on {new Date(selectedQuote.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className={styles.messageBox}>
                        <label>Message to Client</label>
                        <div style={{marginBottom:'1rem', fontSize:'0.9rem', color:'#64748B'}}>
                             Dear {jobData?.username || 'Customer'},
                        </div>
                        <p style={{whiteSpace: 'pre-wrap'}}>{selectedQuote.message || "No specific message provided."}</p>
                        <div style={{marginTop:'1rem', fontSize:'0.9rem', color:'#64748B'}}>
                             Kind regards,<br/>
                             {getBizFromQuote(selectedQuote).businessName}<br/>
                             {getBizFromQuote(selectedQuote).mobile || getBizFromQuote(selectedQuote).phone}
                        </div>
                    </div>
                </div>
                <div className={styles.modalActions}>
                    <button 
                      onClick={() => openBusinessProfile(getBizFromQuote(selectedQuote).businessId)} 
                      className={styles.btnSecondary}
                    >
                      <Info size={18}/> Review Profile
                    </button>
                    {!isAssigned && !isCompleted && (
                        <button 
                          onClick={handleAssignJob} 
                          className={styles.btnPrimary} 
                          disabled={isAssigning}
                        >
                          {isAssigning ? 'Processing...' : 'Accept Quote'}
                        </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* 3. BROWSE PROS */}
      <BrowseProsModal 
          isOpen={isBrowseProsOpen}
          onClose={() => setIsBrowseProsOpen(false)}
          taskId={inviteTaskId} 
          projectId={inviteProjectId} 
          locationId={inviteLocationId} 
          professionId={jobData?.profession?.[0]?.id} 
          trade={jobData?.profession?.[0]?.name}
          postcode={jobData?.postcode}
          onInviteSent={() => fetchData()} 
          jobId={jobId}
          leads={leads}
      />

      {/* 4. BUSINESS PROFILE PANEL */}
      <BusinessProfilePureCSS
          isOpen={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          businessId={selectedBusinessId}
          useIDTYPE={"BID"}
      />
    </div>
  );
};

export default JobManagementPage;